import React from "react";
import { FiChevronDown, FiRotateCcw, FiUser } from "react-icons/fi";

import { MONTHS } from "./constants";

type Option = { value: string; label: string };

/**
 * One select inside a segmented control: the native element is kept (so it
 * behaves natively on every device, including the mobile wheel picker) but
 * stripped of its chrome, with the value drawn as the label and a single
 * chevron on top.
 */
function SelectChip({
  value,
  onChange,
  options,
  ariaLabel,
  icon,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly Option[];
  ariaLabel: string;
  icon?: React.ReactNode;
}) {
  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative flex h-full items-center hover:bg-gray-50">
      {icon && (
        <span className="pointer-events-none absolute left-2 text-gray-400">
          {icon}
        </span>
      )}

      <span
        className={`pointer-events-none block max-w-[7.5rem] truncate whitespace-nowrap pr-6 text-xs font-medium leading-7 text-gray-800 ${
          icon ? "pl-7" : "pl-2.5"
        }`}
      >
        {selected?.label ?? value}
      </span>

      <FiChevronDown
        size={13}
        className="pointer-events-none absolute right-2 text-gray-400"
      />

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent text-transparent opacity-0 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const shell =
  "inline-flex h-7 items-center overflow-hidden rounded-md border border-gray-200 bg-white transition-colors focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-500/20 hover:border-gray-300";

export default function FilterRow({
  title,
  month,
  year,
  onMonthChange,
  onYearChange,
  yearOptions,
  employee,
}: {
  title: string;
  month: string;
  year: number;
  onMonthChange: (m: string) => void;
  onYearChange: (y: number) => void;
  yearOptions: number[];
  /** Optional extra filter, shown in the same bar. */
  employee?: {
    value: string;
    onChange: (v: string) => void;
    options: Option[];
  };
}) {
  const now = new Date();
  const thisMonth = String(now.getMonth() + 1).padStart(2, "0");
  const thisYear = now.getFullYear();
  const isCurrentPeriod = month === thisMonth && year === thisYear;

  const yearAsOptions = React.useMemo(
    () => yearOptions.map((y) => ({ value: String(y), label: String(y) })),
    [yearOptions]
  );

  const employeeOptions = React.useMemo(
    () =>
      employee ? [{ value: "", label: "All employees" }, ...employee.options] : [],
    [employee]
  );

  return (
    <div aria-label={`${title} filter`} className="flex items-center gap-1.5">
      {!isCurrentPeriod && (
        <button
          type="button"
          onClick={() => {
            onMonthChange(thisMonth);
            onYearChange(thisYear);
          }}
          title="Reset to this month"
          aria-label="Reset to this month"
          className="grid h-7 w-7 place-items-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <FiRotateCcw size={13} />
        </button>
      )}

      {/* Month and year read as one period control, not two loose dropdowns. */}
      <div className={shell}>
        <SelectChip
          value={month}
          onChange={onMonthChange}
          options={MONTHS}
          ariaLabel="Month"
        />
        <span className="h-3.5 w-px bg-gray-200" />
        <SelectChip
          value={String(year)}
          onChange={(v) => onYearChange(Number(v))}
          options={yearAsOptions}
          ariaLabel="Year"
        />
      </div>

      {employee && (
        <div className={shell}>
          <SelectChip
            value={employee.value}
            onChange={employee.onChange}
            options={employeeOptions}
            ariaLabel="Employee"
            icon={<FiUser size={12} />}
          />
        </div>
      )}
    </div>
  );
}
